export type VideoPlaybackState = "playing" | "paused";

/**
 * Parse a postMessage payload into an object, or null if it is not JSON.
 */
function asRecord(data: unknown): Record<string, unknown> | null {
	if (typeof data === "string") {
		try {
			const parsed: unknown = JSON.parse(data);
			if (parsed && typeof parsed === "object") {
				return parsed as Record<string, unknown>;
			}
		} catch {
			return null;
		}
		return null;
	}
	if (data && typeof data === "object") {
		return data as Record<string, unknown>;
	}
	return null;
}

/**
 * Hostname without a leading www, or null if `value` is not a valid URL.
 */
function hostFromUrl(value: string): string | null {
	try {
		return new URL(value).hostname.replace(/^www\./, "");
	} catch {
		return null;
	}
}

function isYouTubeHost(host: string): boolean {
	return host === "youtube.com" || host === "youtube-nocookie.com";
}

/**
 * Extract a YouTube video id from an embed URL, or null.
 */
export function getYouTubeVideoIdFromEmbedUrl(embedSrc: string): string | null {
	try {
		const url = new URL(embedSrc);
		if (!isYouTubeHost(url.hostname.replace(/^www\./, ""))) {
			return null;
		}
		const id = url.pathname.split("/embed/")[1]?.split("/")[0]?.split("?")[0];
		return id || null;
	} catch {
		return null;
	}
}

/**
 * Prefer the privacy-enhanced YouTube embed host for inline previews.
 */
export function toYouTubeNoCookieEmbedUrl(embedSrc: string): string {
	const url = new URL(embedSrc);
	if (url.hostname.replace(/^www\./, "") === "youtube.com") {
		url.hostname = "www.youtube-nocookie.com";
	}
	return url.toString();
}

/**
 * True when the message came from YouTube or Vimeo, not an unrelated window.
 */
export function isTrustedVideoEmbedOrigin(origin: string): boolean {
	const host = hostFromUrl(origin);
	return (
		host === "youtube.com" ||
		host === "youtube-nocookie.com" ||
		host === "vimeo.com" ||
		host === "player.vimeo.com"
	);
}

/**
 * Map a YouTube playerState / onStateChange value to playing or paused.
 */
function youtubeState(info: unknown): VideoPlaybackState | null {
	const state =
		typeof info === "number"
			? info
			: info && typeof info === "object"
				? (info as {playerState?: unknown}).playerState
				: undefined;
	if (typeof state !== "number") return null;
	if (state === 1 || state === 3) return "playing";
	if (state === 0 || state === 2 || state === 5) return "paused";
	return null;
}

/**
 * Read a YouTube or Vimeo postMessage and return playing, paused, or null.
 */
export function playbackStateFromEmbedMessage(
	data: unknown
): VideoPlaybackState | null {
	const payload = asRecord(data);
	if (!payload) return null;

	const event = payload.event;
	if (event === "play") return "playing";
	if (event === "pause" || event === "ended" || event === "finish") {
		return "paused";
	}

	if (event === "onStateChange" || event === "infoDelivery") {
		return youtubeState(payload.info);
	}

	return null;
}

/**
 * Add the query flags YouTube/Vimeo need so the iframe can receive API commands.
 */
export function withEmbedApiParams(embedSrc: string, origin?: string): string {
	const url = new URL(embedSrc);
	const host = url.hostname.replace(/^www\./, "");
	if (host === "youtube.com" || host === "youtube-nocookie.com") {
		url.searchParams.set("enablejsapi", "1");
		if (origin) url.searchParams.set("origin", origin);
	} else if (host === "vimeo.com" || host === "player.vimeo.com") {
		url.searchParams.set("api", "1");
	}
	return url.toString();
}

/**
 * Share of the player — or of the viewport, when the player is taller than
 * the screen — that must be visible before the muted preview may start.
 * Starting on a sliver would still play the beginning off-screen.
 */
export const MUTED_PREVIEW_VISIBILITY_RATIO = 0.5;

type MutedPreviewVisibility = {
	isIntersecting: boolean;
	intersectionRatio: number;
	intersectionRect: {height: number};
	rootBounds: {height: number} | null;
};

/**
 * True when the muted inline preview should start. Latches in the player:
 * leaving the viewport later must not restart the clip.
 */
export function shouldStartMutedPreview(
	entry: MutedPreviewVisibility
): boolean {
	if (!entry.isIntersecting) return false;
	if (entry.intersectionRatio >= MUTED_PREVIEW_VISIBILITY_RATIO) return true;

	const viewportHeight = entry.rootBounds?.height ?? 0;
	if (viewportHeight <= 0) return false;
	return (
		entry.intersectionRect.height >=
		viewportHeight * MUTED_PREVIEW_VISIBILITY_RATIO
	);
}

/**
 * Add autoplay. Pass `muted: true` for inline previews (browsers block
 * unmuted autoplay without a click). Muted previews also hide player chrome
 * so our overlay is the only hover UI. Modal playback should stay unmuted.
 *
 * Pass `autoplay: false` for the still frame shown before the player is in
 * view. That drops any autoplay flag already on the URL.
 *
 * Always set `playsinline=1` for YouTube/Vimeo: iOS Safari will not autoplay
 * (or will force fullscreen) in an iframe without it, even after a tap.
 */
export function withEmbedAutoplay(
	embedSrc: string,
	{muted = false, autoplay = true}: {muted?: boolean; autoplay?: boolean} = {}
): string {
	const url = new URL(embedSrc);
	const host = url.hostname.replace(/^www\./, "");
	if (autoplay) {
		url.searchParams.set("autoplay", "1");
	} else {
		url.searchParams.delete("autoplay");
	}
	if (host === "vimeo.com" || host === "player.vimeo.com") {
		url.searchParams.set("muted", muted ? "1" : "0");
		url.searchParams.set("playsinline", "1");
		if (muted) {
			url.searchParams.set("controls", "0");
			// Avoid `background=1` so the same player can be unmuted on tap
			// (iOS cannot autoplay a freshly mounted unmuted embed).
		}
	} else {
		url.searchParams.set("mute", muted ? "1" : "0");
		// Required for modal autoplay on iOS, not only muted previews.
		url.searchParams.set("playsinline", "1");
		if (muted) {
			url.searchParams.set("controls", "0");
			url.searchParams.set("cc_load_policy", "0");
			url.searchParams.set("disablekb", "1");
			url.searchParams.set("fs", "0");
			url.searchParams.set("iv_load_policy", "3");
			url.searchParams.set("modestbranding", "1");
			url.searchParams.set("rel", "0");
		}
	}
	return url.toString();
}

/**
 * Muted inline preview: autoplay with player chrome minimized where YouTube/Vimeo allow.
 */
export function withEmbedPreviewParams(embedSrc: string): string {
	const url = new URL(withEmbedAutoplay(embedSrc, {muted: true}));
	const host = url.hostname.replace(/^www\./, "");

	if (isYouTubeHost(host)) {
		const videoId = getYouTubeVideoIdFromEmbedUrl(url.toString());
		if (videoId) {
			url.searchParams.set("loop", "1");
			url.searchParams.set("playlist", videoId);
		}
	}

	return url.toString();
}

/**
 * Subscribe the embed to play/pause/ended events via postMessage.
 */
export function startEmbedPlaybackListener(
	win: Window,
	embedSrc: string
): void {
	const host = hostFromUrl(embedSrc);
	if (host === "vimeo.com" || host === "player.vimeo.com") {
		win.postMessage(
			JSON.stringify({method: "addEventListener", value: "play"}),
			"*"
		);
		win.postMessage(
			JSON.stringify({method: "addEventListener", value: "pause"}),
			"*"
		);
		win.postMessage(
			JSON.stringify({method: "addEventListener", value: "ended"}),
			"*"
		);
		return;
	}
	win.postMessage(JSON.stringify({event: "listening"}), "*");
}

/**
 * Tell the YouTube or Vimeo iframe to start playback.
 */
export function playEmbedCommand(win: Window, embedSrc: string): void {
	const host = hostFromUrl(embedSrc);
	if (host === "vimeo.com" || host === "player.vimeo.com") {
		win.postMessage(JSON.stringify({method: "play"}), "*");
		return;
	}
	win.postMessage(
		JSON.stringify({event: "command", func: "playVideo", args: []}),
		"*"
	);
}

/**
 * Unmute an already-loaded embed. Must run inside a user-gesture handler on
 * iOS — mounting a new unmuted iframe after setState will not autoplay.
 */
export function unmuteEmbedCommand(win: Window, embedSrc: string): void {
	const host = hostFromUrl(embedSrc);
	if (host === "vimeo.com" || host === "player.vimeo.com") {
		win.postMessage(JSON.stringify({method: "setMuted", value: false}), "*");
		win.postMessage(JSON.stringify({method: "setVolume", value: 1}), "*");
		return;
	}
	win.postMessage(
		JSON.stringify({event: "command", func: "unMute", args: []}),
		"*"
	);
	win.postMessage(
		JSON.stringify({event: "command", func: "setVolume", args: [100]}),
		"*"
	);
}

/**
 * Remute an embed when returning to the muted inline preview.
 */
export function muteEmbedCommand(win: Window, embedSrc: string): void {
	const host = hostFromUrl(embedSrc);
	if (host === "vimeo.com" || host === "player.vimeo.com") {
		win.postMessage(JSON.stringify({method: "setMuted", value: true}), "*");
		win.postMessage(JSON.stringify({method: "setVolume", value: 0}), "*");
		return;
	}
	win.postMessage(
		JSON.stringify({event: "command", func: "mute", args: []}),
		"*"
	);
}
